import { useEffect } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { UserId } from './types'

const EXPOSED_KEY = 'footage_exposed_flags'

function markExposedLocally(flagKey: string): boolean {
  try {
    const raw = sessionStorage.getItem(EXPOSED_KEY)
    const set = new Set<string>(raw ? JSON.parse(raw) : [])
    if (set.has(flagKey)) return false
    set.add(flagKey)
    sessionStorage.setItem(EXPOSED_KEY, JSON.stringify([...set]))
    return true
  } catch {
    return true
  }
}

export function useFeatureFlag(
  flagKey: string,
  userId?: UserId,
  // skip: don't evaluate the flag or record an exposure. Use when the caller
  // knows the user is ineligible for the experiment — an exposure would
  // pollute the assignment pool with users who can never see the treatment.
  opts?: { skip?: boolean }
): boolean | string | null | undefined {
  const skip = opts?.skip === true
  const value = useQuery(api.featureFlags.check, skip ? 'skip' : { flagKey, userId })
  const recordExposure = useMutation(api.featureFlags.recordExposure)

  useEffect(() => {
    if (skip || value === undefined || value === null) return
    if (!markExposedLocally(flagKey)) return
    recordExposure({ flagKey }).catch(() => {})
  }, [flagKey, value, recordExposure, skip])

  return value
}

export const useFF = useFeatureFlag
export const FF = useFeatureFlag
